import { AuthenticationRequiredError, EnterHttpError } from '@enter/api';
import { datadogLogger } from '@/lib/datadog/consent';
import { getAppVersion } from '@/lib/app-origin';
import {
    getConsentSnapshot,
    subscribeConsent,
    type ConsentSnapshot,
} from '@/contexts/consent/store';

export interface BootstrapAttempt {
    id: string;
    responseReceived: boolean;
    httpStatus?: number;
    serverTraceId?: string;
}

export const BOOTSTRAP_ENDPOINTS = {
    user_info: '/users/info',
    business_access: '/api/compliance/access-check',
} as const;
type BootstrapStage = keyof typeof BOOTSTRAP_ENDPOINTS;

export class BootstrapDiagnosticError extends Error {
    constructor(readonly diagnosticId: string) {
        super('Account bootstrap failed');
        this.name = 'BootstrapDiagnosticError';
    }
}

/** 两个原生启动依赖共用的明确 HTTP 失败证据；不根据错误文字猜测。 */
export function isBootstrapDependencyHTTPFailure(error: unknown): boolean {
    if (!(error instanceof EnterHttpError) || !Number.isInteger(error.status)) return false;
    const status = error.status;
    return status !== undefined && ((status >= 500 && status <= 599) || status === 408 || status === 429);
}

function numericCode(body: unknown): number | undefined {
    if (!body || typeof body !== 'object' || !('code' in body)) return undefined;
    return typeof body.code === 'number' && Number.isFinite(body.code) ? body.code : undefined;
}

function safeTraceId(value: string | null | undefined): string | undefined {
    return value && /^[a-zA-Z0-9-]{1,80}$/.test(value) ? value : undefined;
}

function diagnosticCause(error: unknown): { category?: string; status?: number; code?: string } {
    const cause =
        error instanceof AuthenticationRequiredError || error instanceof EnterHttpError
            ? error.cause
            : undefined;
    if (!cause || typeof cause !== 'object') return {};
    const value = cause as Record<string, unknown>;
    return {
        category:
            typeof value.category === 'string' &&
            ['network', 'aborted', 'server', 'http', 'invalid_response'].includes(value.category)
                ? value.category
                : undefined,
        status:
            typeof value.status === 'number' &&
            Number.isInteger(value.status) &&
            value.status >= 100 &&
            value.status <= 599
                ? value.status
                : undefined,
        code:
            typeof value.code === 'string' &&
            ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_CANCELED'].includes(value.code)
                ? value.code
                : undefined,
    };
}

function classifyBootstrapFailure(error: unknown, responseReceived: boolean): string {
    if (error instanceof AuthenticationRequiredError) return 'token_acquisition';
    if (error instanceof EnterHttpError && error.status) return 'http';
    if (error instanceof EnterHttpError && numericCode(error.responseBody) !== undefined)
        return 'business';
    if (responseReceived) return 'invalid_response';
    if (error instanceof EnterHttpError || error instanceof TypeError) return 'network';
    return 'unexpected';
}

async function reportBootstrapFailure(context: Record<string, unknown>): Promise<void> {
    let consent: ConsentSnapshot | null = getConsentSnapshot();
    if (consent.resolution === 'pending') {
        // Account bootstrap can fail before the independent consent initialization finishes.
        consent = await new Promise<ConsentSnapshot | null>(resolve => {
            const finish = (result: ConsentSnapshot | null) => {
                clearTimeout(timeout);
                unsubscribe();
                resolve(result);
            };
            const unsubscribe = subscribeConsent(() => {
                const current = getConsentSnapshot();
                if (current.resolution !== 'pending') finish(current);
            });
            const timeout = setTimeout(() => finish(null), 10_000);
        });
    }
    if (!consent) {
        throw new Error('Account bootstrap diagnostic consent initialization timed out');
    }
    if (!consent.analyticsAllowed) return;
    datadogLogger.error('account_bootstrap_failed', context);
}

/** 仅发送固定字段；原始错误可能包含令牌、响应正文或账号信息。 */
export async function runBootstrapCheck<T>(
    stage: BootstrapStage,
    check: (attempt: BootstrapAttempt) => Promise<T>,
    signal?: AbortSignal
): Promise<T> {
    const attempt: BootstrapAttempt = { id: crypto.randomUUID(), responseReceived: false };
    const started = performance.now();
    try {
        return await check(attempt);
    } catch (error) {
        if (signal?.aborted) throw error;
        const httpError = error instanceof EnterHttpError ? error : undefined;
        const status = httpError?.status ?? attempt.httpStatus;
        const businessCode = numericCode(httpError?.responseBody);
        const reason = classifyBootstrapFailure(error, attempt.responseReceived);
        const cause = diagnosticCause(error);
        const context = {
            error_id: attempt.id,
            stage,
            endpoint: BOOTSTRAP_ENDPOINTS[stage],
            reason,
            http_status: status,
            cause_category: cause.category,
            cause_http_status: cause.status,
            transport_code: cause.code,
            business_code: businessCode,
            server_trace_id: safeTraceId(
                httpError?.headers?.get('X-Trace-ID') ?? attempt.serverTraceId
            ),
            duration_ms: Math.round(performance.now() - started),
            version: getAppVersion(),
            online: navigator.onLine,
        };
        // 不等待监控上传；监控故障不能覆盖原始失败，也不能放行页面。
        void reportBootstrapFailure(context).catch(() => {
            console.warn('account_bootstrap_diagnostic_delivery_failed', { error_id: attempt.id });
        });
        throw new BootstrapDiagnosticError(attempt.id);
    }
}
